import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AppLogoHeader,
  AppLogoHeaderLight,
  DefaultPlaceholderImage,
} from 'components/_common/Icons';
import AvatarSvg from 'components/_common/Icons/SVGIcons/AvatarSvg';
import MagnifierSvg from 'components/_common/Icons/SVGIcons/MagnifierSvg';
import { getStringPrefix, placeParamsToURL } from 'helpers/CommonHelper';
import { getImageURL } from 'helpers/FileUploadHelper';
import { t } from 'i18next';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import { userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import { getGlobalSearch } from 'store/customer/actions/CustomerProductAction';
import { appThemeSelector } from 'store/customer/selectors/CustomerGlobalSelectors';
import { useAppSelector } from 'store/hooks';
import { PRODUCT_FILTER_QUERY_KEYS } from 'utils/productQuery';

import LoginModal from '../Modals/LoginModal';
import AdditionalMenuSection from '../Sections/AdditionalMenuSection';
import DeliveryLocationBlock from '../Sections/DeliveryLocationBlock';
import MinCartSection from '../Sections/MinCartSection';
import ProfileDropdown from '../Sections/ProfileDropdown';
// import WishListSection from '../Sections/WishListSection';

interface MiddleSectionProps {
  // header is currently see-through (top of home / FFL detail), so it sits over the hero image
  isTransparent?: boolean;
}

const MiddleSection: React.FC<MiddleSectionProps> = ({ isTransparent = false }) => {
  const [searchValue, setSearchValue] = useState('');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const loginWrapperRef = useRef<HTMLDivElement>(null);
  const userProfileInfo = useAppSelector(userProfileInfoSelector);
  const theme = useAppSelector(appThemeSelector);

  const location = useLocation();
  const navigate = useNavigate();

  const isFFLPage =
    location.pathname.includes('/ffl-listing') || location.pathname.includes('/ffl-locator');
  const placeholderText = isFFLPage
    ? t('search_ffl_placeholder', 'Search by FFL Dealer Name')
    : t('search_placeholder');

  // Sync search from URL when location.search changes
  useEffect(() => {
    const urlSearch = new URLSearchParams(location.search).get('search') || '';
    setSearchValue(urlSearch);
  }, [location.search]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchWrapperRef]);

  // Close the login/signup modal on outside click
  useEffect(() => {
    if (!isGuestModalOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (loginWrapperRef.current && !loginWrapperRef.current.contains(event.target as Node)) {
        setIsGuestModalOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGuestModalOpen]);

  // Handle Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setShowSearchDropdown(false);
      }
    }
    if (showSearchDropdown) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSearchDropdown]);

  // Live debounced API search
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearchLoading(false);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearchLoading(true);
    try {
      const response: any = await getGlobalSearch({
        search: query,
        entities: ['products'],
        page: 1,
        limit: 5,
      });

      if (response && response.status && response.data?.products?.results) {
        setSearchResults(response.data.products.results);
      } else {
        setSearchResults([]);
      }
      setShowSearchDropdown(true);
    } catch (error) {
      console.error('Customer search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        performSearch(query);
      }, 350),
    [performSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);
    debouncedSearch(val);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchResults([]);
    setShowSearchDropdown(false);
    debouncedSearch.cancel();
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has(PRODUCT_FILTER_QUERY_KEYS.SEARCH)) {
      currentParams.delete(PRODUCT_FILTER_QUERY_KEYS.SEARCH);
      navigate(`${location.pathname}?${currentParams.toString()}`);
    }
  };

  const handleSearchSubmit = useCallback(() => {
    setShowSearchDropdown(false);
    debouncedSearch.cancel();
    if (!searchValue.trim()) {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has(PRODUCT_FILTER_QUERY_KEYS.SEARCH)) {
        currentParams.delete(PRODUCT_FILTER_QUERY_KEYS.SEARCH);
        navigate(`${location.pathname}?${currentParams.toString()}`);
      }
      return;
    }
    navigate(
      `${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.SEARCH}=${encodeURIComponent(searchValue.trim())}`
    );
  }, [debouncedSearch, location.pathname, navigate, searchValue]);

  return (
    <div className="middle-section">
      <div className="w-100 d-flex justify-content-between align-items-center gap-3">
        <div className="middle-section__left">
          {/* Logo */}
          <a href="/" className="middle-section__logo">
            <img
              className="logo-image"
              src={!isTransparent && theme === 'light' ? AppLogoHeaderLight : AppLogoHeader}
              alt="AppLogoHeader"
            />
          </a>

          {/* Delivery Location */}
          <div className="middle-section__delivery">
            <DeliveryLocationBlock />
          </div>

          {/* Search Bar */}
          <div className="middle-section__search position-relative" ref={searchWrapperRef}>
            <input
              type="text"
              placeholder={placeholderText}
              value={searchValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchSubmit();
                }
              }}
              onFocus={() => {
                if (searchValue.trim()) setShowSearchDropdown(true);
              }}
              aria-label={t('search')}
              style={searchValue ? { paddingRight: '88px' } : undefined}
            />
            {isSearchLoading ? (
              <div className="search-btn d-flex align-items-center justify-content-center">
                <Spinner animation="border" size="sm" />
              </div>
            ) : searchValue ? (
              <div
                className="search-btn d-flex align-items-center justify-content-center px-2 gap-2"
                style={{ width: 'auto', minWidth: '64px' }}
              >
                <button
                  type="button"
                  className="bg-transparent border-0 text-white p-0 d-flex align-items-center justify-content-center shadow-none"
                  aria-label={t('clear', 'Clear')}
                  onClick={handleClearSearch}
                  title={t('clear', 'Clear')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
                <span className="text-white opacity-50">|</span>
                <button
                  type="button"
                  className="bg-transparent border-0 text-white p-0 d-flex align-items-center justify-content-center shadow-none"
                  aria-label={t('search')}
                  onClick={handleSearchSubmit}
                  title={t('search')}
                >
                  <MagnifierSvg />
                </button>
              </div>
            ) : (
              <button
                className="search-btn"
                aria-label={t('search')}
                type="button"
                onClick={handleSearchSubmit}
              >
                <MagnifierSvg />
              </button>
            )}

            {/* Search Dropdown */}
            {showSearchDropdown && searchValue.trim() && (
              <div className="customer-search-dropdown position-absolute top-100 start-0 w-100 border rounded shadow mt-1 overflow-auto z-3">
                {isSearchLoading && searchResults.length === 0 ? (
                  <div className="p-4 text-center text-white">
                    <Spinner animation="border" variant="light" size="sm" />
                    <div className="mt-2 small">{t('loading', 'Loading...')}</div>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-center text-white small">
                    {t('no_results_found', 'No results found')}
                  </div>
                ) : (
                  <ul className="list-unstyled m-0 p-0">
                    {searchResults.map((item) => {
                      const categoryName = item.category?.name || '';
                      const isAccessories = categoryName.toLowerCase().includes('accessor');
                      return (
                        <li
                          key={`cust-search-${item.id}`}
                          className="customer-search-item px-3 py-2 d-flex align-items-center justify-content-between gap-3 border-bottom cursor-pointer"
                          onClick={() => {
                            setShowSearchDropdown(false);
                            navigate(`/product/${item.slug || item.id}`);
                          }}
                        >
                          <div className="d-flex align-items-center gap-3 overflow-hidden">
                            <img
                              src={
                                item.featured_image
                                  ? getImageURL(item.featured_image, 'sm')
                                  : DefaultPlaceholderImage
                              }
                              alt={item.name || 'Product'}
                              className="item-thumbnail rounded object-fit-contain bg-light flex-shrink-0"
                              style={{ width: '40px', height: '40px' }}
                            />
                            <div className="item-content overflow-hidden">
                              <p className="item-title m-0 text-white text-truncate fw-medium small">
                                {item.name || 'Unknown Product'}
                              </p>
                              {item.sku && (
                                <span className="text-muted d-block" style={{ fontSize: '11px' }}>
                                  SKU: {item.sku}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex-shrink-0">
                            {isAccessories ? (
                              <span
                                className="badge bg-secondary px-2 py-1 fw-bold"
                                style={{ fontSize: '11px' }}
                              >
                                {categoryName || 'Accessories'}
                              </span>
                            ) : (
                              <span
                                className="badge text-white px-2 py-1 fw-bold"
                                style={{ backgroundColor: '#ff6900', fontSize: '11px' }}
                              >
                                {categoryName || 'Firearm'}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Action Icons */}
        <div className="middle-section__actions">
          <div className="middle-section__btn_group">
            {/* Hamburger (menu) */}
            <AdditionalMenuSection />

            {/* Wishlist */}
            {/* <WishListSection /> */}

            {/* Cart */}
            <MinCartSection />

            {/* Account */}
            <div className="middle-section__account-wrapper ms-1">
              {userProfileInfo ? (
                <button
                  className="middle-section__icon-btn profile-avatar-btn"
                  aria-label={t('account')}
                  onClick={() => setIsProfileOpen((prev) => !prev)}
                >
                  {userProfileInfo.profile_pic ? (
                    <img
                      src={getImageURL(userProfileInfo.profile_pic)}
                      alt="User Profile"
                      className="profile-avatar-img"
                    />
                  ) : (
                    <div className="profile-avatar-initials">
                      {getStringPrefix(
                        `${userProfileInfo.first_name} ${userProfileInfo.last_name}`
                      )}
                    </div>
                  )}
                </button>
              ) : (
                <button
                  className="middle-section__icon-btn d-none"
                  aria-label={t('account')}
                  onClick={() => setIsProfileOpen((prev) => !prev)}
                >
                  <AvatarSvg />
                </button>
              )}

              {isProfileOpen && <ProfileDropdown onClose={() => setIsProfileOpen(false)} />}
            </div>
          </div>

          {/* Login/Signup */}
          {!userProfileInfo && (
            <div className="middle-section__login-wrapper" ref={loginWrapperRef}>
              <button
                className="middle-section__login-btn"
                onClick={() => setIsGuestModalOpen((prev) => !prev)}
              >
                {t('login_signup')}
              </button>
              {isGuestModalOpen && <LoginModal onClose={() => setIsGuestModalOpen(false)} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiddleSection;
